import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { PERMISSIONS, priorityClass, priorityLabel, statusClass, statusLabel } from '../constants';
import { formatDateTime, formatRelative, fromDateInput, toDateInput } from '../format';
import type { HistoryEntry, Task, TaskHistoryResponse, TaskPatchBody } from '../types';
import { ErrorBox, InlineLoading } from './Feedback';
import { ConfirmButton } from './ConfirmButton';
import { HistoryList } from './HistoryList';

export interface TaskDetailPanelProps {
  task: Task;
  permissions: string[];
  /** Ändert sich bei Realtime-Ereignissen und lädt die Historie neu. */
  revision: number;
  onClose: () => void;
  onPatch: (id: string, patch: TaskPatchBody) => Promise<boolean>;
  onDelete: (task: Task) => void;
}

export function TaskDetailPanel({
  task,
  permissions,
  revision,
  onClose,
  onPatch,
  onDelete,
}: TaskDetailPanelProps): ReactNode {
  const canViewHistory = permissions.includes(PERMISSIONS.HISTORY_VIEW);
  const canUpdate = permissions.includes(PERMISSIONS.TASK_UPDATE);
  const canDelete = permissions.includes(PERMISSIONS.TASK_DELETE);

  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(canViewHistory);
  const [historyNonce, setHistoryNonce] = useState(0);

  const [comment, setComment] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDescription, setEditDescription] = useState(task.description);
  const [editDueDate, setEditDueDate] = useState(toDateInput(task.dueAt));
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setEditTitle(task.title);
    setEditDescription(task.description);
    setEditDueDate(toDateInput(task.dueAt));
  }, [task.id, task.title, task.description, task.dueAt]);

  useEffect(() => {
    if (!canViewHistory) {
      setHistory(null);
      setHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    api
      .get<TaskHistoryResponse>(`/api/tasks/${task.id}/history`)
      .then((result) => {
        if (cancelled) return;
        setHistory(result.history);
        setHistoryLoading(false);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setHistoryError(errorMessage(cause));
        setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [task.id, canViewHistory, revision, historyNonce]);

  const saveEdit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (editBusy) return;
    if (editTitle.trim() === '') {
      setEditError('Der Titel darf nicht leer sein.');
      return;
    }
    setEditBusy(true);
    setEditError(null);
    const ok = await onPatch(task.id, {
      title: editTitle.trim(),
      description: editDescription,
      dueAt: fromDateInput(editDueDate),
    });
    setEditBusy(false);
    if (ok) setEditing(false);
  };

  const submitComment = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (commentBusy) return;
    const text = comment.trim();
    if (text === '') return;
    setCommentBusy(true);
    setCommentError(null);

    const body: TaskPatchBody = { note: text };
    // Backend-Eigenheit: `PATCH /api/tasks/:id` kehrt früh zurück, wenn der Body
    // ausser `note` kein "getracktes" Feld enthält — der Kommentar ginge dann
    // verloren. Ein unveränderter Titel macht den Patch nicht-leer, ohne die
    // Historie zu verfälschen (from === to wird nicht protokolliert).
    if (canUpdate) body.title = task.title;

    const ok = await onPatch(task.id, body);
    setCommentBusy(false);
    if (ok) {
      setComment('');
      setHistoryNonce((value) => value + 1);
    } else {
      setCommentError('Kommentar konnte nicht gespeichert werden.');
    }
  };

  return (
    <aside className="detail-panel" aria-label="Aufgabendetails">
      <header className="detail-head">
        <div className="detail-head-text">
          <div className="detail-badges">
            <span className={`badge ${statusClass(task.status)}`}>{statusLabel(task.status)}</span>
            <span className={`badge ${priorityClass(task.priority)}`}>
              {priorityLabel(task.priority)}
            </span>
          </div>
          <h2>{task.title}</h2>
        </div>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Panel schließen">
          ✕
        </button>
      </header>

      <div className="detail-body">
        <dl className="detail-meta">
          <div>
            <dt>Zugewiesen</dt>
            <dd>{task.assigneeName ?? '—'}</dd>
          </div>
          <div>
            <dt>Fällig</dt>
            <dd>
              {task.dueAt === null
                ? '—'
                : `${formatDateTime(task.dueAt)} (${formatRelative(task.dueAt)})`}
            </dd>
          </div>
          <div>
            <dt>Erstellt von</dt>
            <dd>{task.createdByName ?? '—'}</dd>
          </div>
          <div>
            <dt>Erstellt am</dt>
            <dd>{formatDateTime(task.createdAt)}</dd>
          </div>
          <div>
            <dt>Zuletzt geändert</dt>
            <dd>{formatDateTime(task.updatedAt)}</dd>
          </div>
          {task.completedAt !== null && (
            <div>
              <dt>Erledigt am</dt>
              <dd>{formatDateTime(task.completedAt)}</dd>
            </div>
          )}
        </dl>

        {canUpdate && !editing && (
          <div className="detail-section-head">
            <h3>Beschreibung</h3>
            <button type="button" className="button button-ghost button-small" onClick={() => setEditing(true)}>
              Bearbeiten
            </button>
          </div>
        )}

        {editing ? (
          <form className="form-grid" onSubmit={(event) => void saveEdit(event)}>
            <label className="field">
              <span>Titel *</span>
              <input
                type="text"
                value={editTitle}
                maxLength={200}
                onChange={(event) => setEditTitle(event.target.value)}
              />
            </label>
            <label className="field">
              <span>Beschreibung</span>
              <textarea
                rows={5}
                value={editDescription}
                maxLength={5000}
                onChange={(event) => setEditDescription(event.target.value)}
              />
            </label>
            <label className="field">
              <span>Fällig am</span>
              <input
                type="date"
                value={editDueDate}
                onChange={(event) => setEditDueDate(event.target.value)}
              />
            </label>
            {editError !== null && <div className="feedback feedback-error">{editError}</div>}
            <div className="form-actions">
              <button type="button" className="button button-ghost" onClick={() => setEditing(false)}>
                Abbrechen
              </button>
              <button type="submit" className="button button-primary" disabled={editBusy}>
                {editBusy ? 'Speichern …' : 'Speichern'}
              </button>
            </div>
          </form>
        ) : (
          <p className="detail-description">
            {task.description.trim() === '' ? <span className="muted">Keine Beschreibung.</span> : task.description}
          </p>
        )}

        <section className="detail-section">
          <div className="detail-section-head">
            <h3>Kommentar</h3>
          </div>
          <form onSubmit={(event) => void submitComment(event)} className="comment-form">
            <textarea
              rows={3}
              value={comment}
              maxLength={1000}
              placeholder="Kurzer Kommentar zur Aufgabe …"
              onChange={(event) => setComment(event.target.value)}
            />
            {!canUpdate && (
              <p className="muted small">
                Hinweis: Das Backend speichert Kommentare nur zusammen mit einer Feldänderung
                (<code>task.update</code>).
              </p>
            )}
            {commentError !== null && <div className="feedback feedback-error">{commentError}</div>}
            <div className="form-actions">
              <button type="submit" className="button button-primary button-small" disabled={commentBusy || comment.trim() === ''}>
                {commentBusy ? 'Senden …' : 'Kommentar speichern'}
              </button>
            </div>
          </form>
        </section>

        <section className="detail-section">
          <div className="detail-section-head">
            <h3>Historie</h3>
            <button
              type="button"
              className="button button-ghost button-small"
              disabled={!canViewHistory}
              onClick={() => setHistoryNonce((value) => value + 1)}
            >
              Aktualisieren
            </button>
          </div>

          {!canViewHistory && (
            <div className="feedback feedback-hint">
              Für die Historie fehlt das Recht <code>history.view</code>.
            </div>
          )}
          {canViewHistory && historyLoading && <InlineLoading label="Historie lädt …" />}
          {canViewHistory && historyError !== null && (
            <ErrorBox message={historyError} onRetry={() => setHistoryNonce((value) => value + 1)} />
          )}
          {canViewHistory && !historyLoading && historyError === null && history === null && (
            <p className="muted small">Keine Historieneinträge verfügbar.</p>
          )}
          {canViewHistory && history !== null && history.length === 0 && (
            <p className="muted small">Noch keine Einträge.</p>
          )}
          {canViewHistory && history !== null && history.length > 0 && (
            <HistoryList entries={history} compact />
          )}
        </section>

        {canDelete && (
          <div className="detail-danger">
            <ConfirmButton
              label="Aufgabe löschen"
              question={`"${task.title}" löschen?`}
              onConfirm={() => onDelete(task)}
            />
          </div>
        )}
      </div>
    </aside>
  );
}

